import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const ROLE_LABELS = {
  requestor: 'Requestor',
  supervisor: 'Immediate Supervisor',
  ap_accounting: 'AP Accounting Processor',
  ap_supervisor: 'AP Supervisor',
  accounting_manager: 'Accounting Manager',
  general_manager: 'General Manager',
  owner: 'Owner'
};

const STEP_LABELS = {
  ap_processor: 'AP Processor',
  ap_supervisor: 'AP Supervisor',
  accounting_manager: 'Accounting Manager',
  general_manager: 'General Manager'
};

const emptyUser = {
  username: '',
  full_name: '',
  password: 'password123',
  role: 'requestor',
  department: '',
  default_reviewer_id: '',
  default_approver_id: ''
};

export default function AdminSetupPage() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [assignmentRules, setAssignmentRules] = useState([]);

  const [userForm, setUserForm] = useState(emptyUser);
  const [requestTypeForm, setRequestTypeForm] = useState({ code: '', name: '', description: '' });
  const [documentTypeForm, setDocumentTypeForm] = useState({ code: '', name: '', description: '' });

  const [selectedChecklistType, setSelectedChecklistType] = useState('');
  const [checklistText, setChecklistText] = useState('');

  const [selectedWorkflowType, setSelectedWorkflowType] = useState('');
  const [workflowSteps, setWorkflowSteps] = useState(['ap_processor', 'ap_supervisor']);

  const [selectedAssignmentType, setSelectedAssignmentType] = useState('');
  const [defaultProcessorId, setDefaultProcessorId] = useState('');
  const [defaultSupervisorId, setDefaultSupervisorId] = useState('');

  const processors = useMemo(() => users.filter(u => u.role === 'ap_accounting'), [users]);
  const apSupervisors = useMemo(() => users.filter(u => u.role === 'ap_supervisor'), [users]);
  const staffSupervisors = useMemo(() => users.filter(u => ['supervisor', 'accounting_manager', 'owner'].includes(u.role)), [users]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [options, checklistRes, workflowRes, assignmentRes] = await Promise.all([
        api.get('/admin/options'),
        api.get('/admin/checklist-templates'),
        api.get('/admin/approval-workflows'),
        api.get('/admin/assignment-rules')
      ]);

      setUsers(options.data.users || []);
      setRoles(options.data.roles || []);
      setRequestTypes(options.data.request_types || []);
      setDocumentTypes(options.data.document_types || []);
      setChecklists(checklistRes.data || []);
      setWorkflows(workflowRes.data || []);
      setAssignmentRules(assignmentRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load admin setup');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const seedDefaults = async () => {
    try {
      await api.post('/admin/defaults/seed');
      toast.success('Default request/document types seeded');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed defaults');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/users', userForm);
      toast.success('User created');
      setUserForm(emptyUser);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const createRequestType = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/request-types', requestTypeForm);
      toast.success('Request type created');
      setRequestTypeForm({ code: '', name: '', description: '' });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create request type');
    }
  };

  const createDocumentType = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/document-types', documentTypeForm);
      toast.success('Document type created');
      setDocumentTypeForm({ code: '', name: '', description: '' });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create document type');
    }
  };

  const saveChecklist = async (e) => {
    e.preventDefault();
    if (!selectedChecklistType) return toast.error('Select request type');

    const lines = checklistText.split('\n').map(x => x.trim()).filter(Boolean);
    if (!lines.length) return toast.error('Add at least one checklist item');

    const items = lines.map((line, idx) => {
      const parts = line.split('|').map(x => x.trim());
      const docCode = parts[0];
      const label = parts[1] || parts[0].replaceAll('_', ' ');
      return { document_type_code: docCode, label, required: true, sort_order: idx + 1 };
    });

    try {
      await api.post('/admin/checklist-templates', { request_type_code: selectedChecklistType, items });
      toast.success('Checklist saved');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save checklist');
    }
  };

  const saveWorkflow = async (e) => {
    e.preventDefault();
    if (!selectedWorkflowType) return toast.error('Select request type');

    try {
      await api.post('/admin/approval-workflows', {
        request_type_code: selectedWorkflowType,
        workflow_name: `${selectedWorkflowType.replaceAll('_', ' ')} workflow`,
        steps: workflowSteps,
        active: true
      });
      toast.success('Approval workflow saved');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save workflow');
    }
  };

  const saveAssignmentRule = async (e) => {
    e.preventDefault();
    if (!selectedAssignmentType) return toast.error('Select request type');

    try {
      await api.post('/admin/assignment-rules', {
        request_type_code: selectedAssignmentType,
        default_processor_id: defaultProcessorId || null,
        default_supervisor_id: defaultSupervisorId || null,
        active: true
      });
      toast.success('Approval routing saved');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save approval routing');
    }
  };

  const toggleStep = (step) => {
    setWorkflowSteps(prev => {
      if (prev.includes(step)) return prev.filter(x => x !== step);
      return [...prev, step];
    });
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading admin setup...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-setup-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Admin Setup</h1>
          <p className="text-sm text-muted-foreground">
            Configure users, request types, document checklists, workflows, and approval routing.
          </p>
        </div>
        <Button onClick={seedDefaults} data-testid="seed-defaults-btn">Seed Defaults</Button>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="request-types">Request Types</TabsTrigger>
          <TabsTrigger value="document-types">Document Types</TabsTrigger>
          <TabsTrigger value="checklists">Checklists</TabsTrigger>
          <TabsTrigger value="workflows">Approval Workflow</TabsTrigger>
          <TabsTrigger value="assignments">Approval Routing</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card className="xl:col-span-1">
              <CardHeader><CardTitle>Create User</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={createUser} className="space-y-3">
                  <div><Label>Username</Label><Input value={userForm.username} onChange={e => setUserForm({ ...userForm, username: e.target.value })} required /></div>
                  <div><Label>Full Name</Label><Input value={userForm.full_name} onChange={e => setUserForm({ ...userForm, full_name: e.target.value })} required /></div>
                  <div><Label>Password</Label><Input value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} required /></div>
                  <div>
                    <Label>Role</Label>
                    <Select value={userForm.role} onValueChange={value => setUserForm({ ...userForm, role: value, default_reviewer_id: '' })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roles.map(role => <SelectItem key={role} value={role}>{ROLE_LABELS[role] || role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Department</Label><Input value={userForm.department} onChange={e => setUserForm({ ...userForm, department: e.target.value })} /></div>

                  {userForm.role === 'requestor' && (
                    <div>
                      <Label>Reports To / Immediate Supervisor</Label>
                      <Select value={userForm.default_reviewer_id} onValueChange={value => setUserForm({ ...userForm, default_reviewer_id: value })}>
                        <SelectTrigger><SelectValue placeholder="Select supervisor" /></SelectTrigger>
                        <SelectContent>
                          {staffSupervisors.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Requests submitted by this user will first go to this supervisor.
                      </p>
                    </div>
                  )}

                  <Button type="submit" className="w-full">Create User</Button>
                </form>
              </CardContent>
            </Card>

            <Card className="xl:col-span-2">
              <CardHeader><CardTitle>Users / Employee Hierarchy</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Name</th>
                        <th className="text-left py-2">Username</th>
                        <th className="text-left py-2">Role</th>
                        <th className="text-left py-2">Department</th>
                        <th className="text-left py-2">Reports To</th>
                        <th className="text-left py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id} className="border-b">
                          <td className="py-2">{u.full_name}</td>
                          <td className="py-2">{u.username}</td>
                          <td className="py-2"><Badge variant="secondary">{ROLE_LABELS[u.role] || u.role}</Badge></td>
                          <td className="py-2">{u.department || '-'}</td>
                          <td className="py-2">{users.find(x => x.id === u.default_reviewer_id)?.full_name || '-'}</td>
                          <td className="py-2">{u.is_active ? 'Active' : 'Inactive'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="request-types">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card>
              <CardHeader><CardTitle>Create Request Type</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={createRequestType} className="space-y-3">
                  <div><Label>Code</Label><Input placeholder="reimbursement" value={requestTypeForm.code} onChange={e => setRequestTypeForm({ ...requestTypeForm, code: e.target.value })} required /></div>
                  <div><Label>Name</Label><Input placeholder="Reimbursement" value={requestTypeForm.name} onChange={e => setRequestTypeForm({ ...requestTypeForm, name: e.target.value })} required /></div>
                  <div><Label>Description</Label><Textarea value={requestTypeForm.description} onChange={e => setRequestTypeForm({ ...requestTypeForm, description: e.target.value })} /></div>
                  <Button type="submit" className="w-full">Create Request Type</Button>
                </form>
              </CardContent>
            </Card>
            <Card className="xl:col-span-2">
              <CardHeader><CardTitle>Request Types</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {requestTypes.map(rt => (
                    <div key={rt.code} className="border rounded-md p-3 bg-white">
                      <div className="font-medium">{rt.name}</div>
                      <div className="text-xs text-muted-foreground">{rt.code}</div>
                      {rt.description && <p className="text-sm mt-2">{rt.description}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="document-types">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card>
              <CardHeader><CardTitle>Create Document Type</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={createDocumentType} className="space-y-3">
                  <div><Label>Code</Label><Input placeholder="official_receipt" value={documentTypeForm.code} onChange={e => setDocumentTypeForm({ ...documentTypeForm, code: e.target.value })} required /></div>
                  <div><Label>Name</Label><Input placeholder="Official Receipt" value={documentTypeForm.name} onChange={e => setDocumentTypeForm({ ...documentTypeForm, name: e.target.value })} required /></div>
                  <div><Label>Description</Label><Textarea value={documentTypeForm.description} onChange={e => setDocumentTypeForm({ ...documentTypeForm, description: e.target.value })} /></div>
                  <Button type="submit" className="w-full">Create Document Type</Button>
                </form>
              </CardContent>
            </Card>
            <Card className="xl:col-span-2">
              <CardHeader><CardTitle>Document Types</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {documentTypes.map(dt => (
                    <div key={dt.code} className="border rounded-md p-3 bg-white">
                      <div className="font-medium">{dt.name}</div>
                      <div className="text-xs text-muted-foreground">{dt.code}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="checklists">
          <Card>
            <CardHeader><CardTitle>Checklist Template per Request Type</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={saveChecklist} className="space-y-3">
                <div>
                  <Label>Request Type</Label>
                  <Select value={selectedChecklistType} onValueChange={setSelectedChecklistType}>
                    <SelectTrigger><SelectValue placeholder="Select request type" /></SelectTrigger>
                    <SelectContent>{requestTypes.map(rt => <SelectItem key={rt.code} value={rt.code}>{rt.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Checklist Items</Label>
                  <Textarea
                    className="min-h-40 font-mono text-sm"
                    placeholder={`official_receipt | Official Receipt\nproof_of_payment | Proof of Payment\napproved_form | Approved Expense Form`}
                    value={checklistText}
                    onChange={e => setChecklistText(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Format: document_type_code | Label. One item per line.</p>
                </div>
                <Button type="submit">Save Checklist</Button>
              </form>
              <div className="mt-6 grid md:grid-cols-2 gap-3">
                {checklists.map(cl => (
                  <div key={cl.id} className="border rounded-md p-3 bg-white">
                    <div className="font-medium">{cl.request_type_code}</div>
                    <ul className="mt-2 text-sm list-disc pl-5">
                      {(cl.items || []).map((item, idx) => <li key={`${cl.id}-${idx}`}>{item.label} <span className="text-xs text-muted-foreground">({item.document_type_code})</span></li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workflows">
          <Card>
            <CardHeader><CardTitle>Approval Workflow per Request Type</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={saveWorkflow} className="space-y-4">
                <div>
                  <Label>Request Type</Label>
                  <Select value={selectedWorkflowType} onValueChange={setSelectedWorkflowType}>
                    <SelectTrigger><SelectValue placeholder="Select request type" /></SelectTrigger>
                    <SelectContent>{requestTypes.map(rt => <SelectItem key={rt.code} value={rt.code}>{rt.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Workflow Steps</Label>

                  <div className="border rounded-md p-3 text-sm bg-blue-50 border-blue-300 mt-2">
                    ✓ Immediate Supervisor
                    <div className="text-xs text-muted-foreground mt-1">
                      Fixed first review step. Actual supervisor is pulled from the requestor's Reports To assignment.
                    </div>
                  </div>

                  <div className="grid md:grid-cols-4 gap-2 mt-2">
                    {Object.keys(STEP_LABELS).map(step => (
                      <button
                        key={step}
                        type="button"
                        onClick={() => toggleStep(step)}
                        className={`border rounded-md p-3 text-sm text-left ${workflowSteps.includes(step) ? 'bg-blue-50 border-blue-500' : 'bg-white'}`}
                      >
                        {workflowSteps.includes(step) ? '✓ ' : ''}{STEP_LABELS[step]}
                      </button>
                    ))}
                  </div>
                </div>

                <Button type="submit">Save Workflow</Button>
              </form>

              <div className="mt-6 border rounded-md p-4 bg-slate-50">
                <div className="font-semibold mb-3">Approval Flow Preview</div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-sm">
                  {[
                    ['1', 'Requestor', 'Submit Request'],
                    ['2', 'Immediate Supervisor', 'Review & Approve'],
                    ['3', 'AP Processor', 'Review & Hard Copy'],
                    ['4', 'AP Supervisor', 'Review & Approve'],
                    ['5', 'Accounting Manager', 'Optional'],
                    ['6', 'General Manager', 'Optional']
                  ].map(([num, title, sub]) => (
                    <div key={num} className="border bg-white rounded-md p-3 text-center">
                      <div className="mx-auto mb-2 h-7 w-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">{num}</div>
                      <div className="font-medium">{title}</div>
                      <div className="text-xs text-muted-foreground">{sub}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-3">
                {workflows.map(wf => (
                  <div key={wf.id} className="border rounded-md p-3 bg-white">
                    <div className="font-medium">{wf.request_type_code}</div>
                    <div className="text-xs text-muted-foreground">{wf.workflow_name}</div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge>Immediate Supervisor</Badge>
                      {(wf.steps || []).map(step => <Badge key={step}>{STEP_LABELS[step] || step}</Badge>)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments">
          <Card>
            <CardHeader>
              <CardTitle>Approval Routing</CardTitle>
              <p className="text-sm text-muted-foreground">
                This controls where requests go after the requestor's immediate supervisor approves.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveAssignmentRule} className="grid md:grid-cols-4 gap-3 items-end">
                <div>
                  <Label>Request Type</Label>
                  <Select value={selectedAssignmentType} onValueChange={setSelectedAssignmentType}>
                    <SelectTrigger><SelectValue placeholder="Select request type" /></SelectTrigger>
                    <SelectContent>{requestTypes.map(rt => <SelectItem key={rt.code} value={rt.code}>{rt.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>AP Processor</Label>
                  <Select value={defaultProcessorId} onValueChange={setDefaultProcessorId}>
                    <SelectTrigger><SelectValue placeholder="Select AP Processor" /></SelectTrigger>
                    <SelectContent>{processors.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>AP Supervisor</Label>
                  <Select value={defaultSupervisorId} onValueChange={setDefaultSupervisorId}>
                    <SelectTrigger><SelectValue placeholder="Select AP Supervisor" /></SelectTrigger>
                    <SelectContent>{apSupervisors.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button type="submit">Save Routing</Button>
              </form>

              <div className="mt-6 border rounded-md p-4 bg-slate-50">
                <div className="font-semibold mb-2">Routing Logic</div>
                <div className="text-sm text-muted-foreground">
                  Requestor → Immediate Supervisor from Users tab → AP Processor from this routing rule → AP Supervisor → optional Accounting Manager / General Manager.
                </div>
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-3">
                {assignmentRules.map(rule => {
                  const processor = users.find(u => u.id === rule.default_processor_id);
                  const supervisor = users.find(u => u.id === rule.default_supervisor_id);
                  return (
                    <div key={rule.id} className="border rounded-md p-3 bg-white">
                      <div className="font-medium">{rule.request_type_code}</div>
                      <div className="text-sm mt-2">After Immediate Supervisor Approval:</div>
                      <div className="text-sm mt-1">AP Processor: {processor?.full_name || '-'}</div>
                      <div className="text-sm">AP Supervisor: {supervisor?.full_name || '-'}</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
